/**
 * 从 localStorage 的用户快照合并：用户直授 + 权限组 + 自定义权限
 * 与路由守卫、班级页 mergedPermissions 行为一致
 */
export function mergePermissionObject(target, source) {
  if (!source || typeof source !== 'object') return
  Object.entries(source).forEach(([moduleName, actions]) => {
    if (!Array.isArray(actions)) return
    if (!target[moduleName]) target[moduleName] = new Set()
    actions.forEach((action) => target[moduleName].add(action))
  })
}

/**
 * 与后端 hasPermission 对齐：仅 superadmin 自动放行。
 * admin 角色走权限组，便于通过权限组细化管理员能力。
 */
export function isPrivilegedOperatorRole(userRole) {
  return userRole === 'superadmin'
}

export function getMergedPermissionsFromLocalStorage() {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}')
    const merged = {}
    mergePermissionObject(merged, user.permissions)
    mergePermissionObject(merged, user.permission_group?.permissions)
    mergePermissionObject(merged, user.custom_permissions)
    return Object.fromEntries(
      Object.entries(merged).map(([moduleName, actionSet]) => [moduleName, Array.from(actionSet)])
    )
  } catch {
    return {}
  }
}

export function hasModulePermission(userRole, merged, moduleName, action) {
  if (isPrivilegedOperatorRole(userRole)) return true
  const modulePermissions = merged?.[moduleName] || []
  return modulePermissions.includes('*') || modulePermissions.includes(action)
}

export const REFUND_ACCESS_ACTIONS = [
  'apply_own',
  'apply_all',
  'view_own',
  'upload_voucher',
  'review',
  'settle',
  'view_all'
]

export function hasAnyRefundAccess(userRole, merged) {
  return REFUND_ACCESS_ACTIONS.some(action =>
    hasModulePermission(userRole, merged, 'refunds', action)
  )
}

/**
 * 功能子页面（排课管理、新生测试、题库、班级商城、问卷调查）
 * 未配置 app_pages 的 admin/academic 用户视为旧数据，保持与升级前一致全部放行
 */
export function hasAppPageAccess(userRole, merged, action) {
  if (isPrivilegedOperatorRole(userRole)) return true
  if (userRole === 'academic' || userRole === 'admin') {
    const ap = merged?.app_pages
    if (ap === undefined || ap === null) return true
  }
  return hasModulePermission(userRole, merged, 'app_pages', action)
}

/** 问卷入口：兼容功能页面权限和问卷查看权限 */
export function hasSurveyPageAccess(userRole, merged) {
  return (
    hasAppPageAccess(userRole, merged, 'surveys') ||
    hasModulePermission(userRole, merged, 'surveys', 'view')
  )
}

/** 侧栏「课程」：根据权限动态展示 */
export function canShowCourseMenu(userRole, merged) {
  return hasModulePermission(userRole, merged, 'courses', 'view')
}
